import React, { useState, useEffect } from "react";
import { Lock, CheckCircle2, Radio, Clock } from "lucide-react";
import TeamCrest from "@/components/TeamCrest";
import Countdown from "@/components/Countdown";
import { fmtDateTime } from "@/lib/format";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  scheduled: "In programma", live: "In corso", finished: "Conclusa",
  postponed: "Rinviata", suspended: "Sospesa", cancelled: "Annullata",
};

export default function MatchCard({ match, prediction, canPredict, onConfirmed, onSelect }) {
  const [choice, setChoice] = useState(prediction?.choice || null);
  const [savedChoice, setSavedChoice] = useState(prediction?.choice || null);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(match.locked);

  useEffect(() => {
    if (prediction?.choice) {
      setSavedChoice(prediction.choice);
      setChoice((c) => c ?? prediction.choice);
    }
  }, [prediction?.choice]);

  const dirty = choice && choice !== savedChoice;
  const isFinished = match.status === "finished";
  const isLive = match.status === "live";

  const pick = (val) => {
    setChoice(val);
    onSelect && onSelect(match.id, val !== savedChoice ? val : null);
  };

  const submit = async () => {
    if (!choice || !dirty) return;
    setSaving(true);
    try {
      const { data } = await api.post("/predictions", { match_id: match.id, choice });
      setSavedChoice(choice);
      toast.success("Pronostico confermato");
      onSelect && onSelect(match.id, null);
      onConfirmed && onConfirmed(match.id, data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const Option = ({ val, label }) => {
    const selected = choice === val;
    const isResult = isFinished && match.result === val;
    const wasSaved = savedChoice === val;
    let cls = "border-ink-600 bg-ink-900 text-slate-300 hover:border-gold-300/60";
    if (selected && !locked) cls = "border-gold-300 bg-gold-300 text-ink-900 shadow-goldsm";
    if (locked && wasSaved) cls = isResult ? "border-status-correct bg-status-correct/20 text-status-correct" : "border-ink-600 bg-ink-700 text-slate-300";
    if (isFinished && isResult) cls = "border-status-correct bg-status-correct/15 text-status-correct";
    return (
      <button
        disabled={locked || saving}
        onClick={() => pick(val)}
        data-testid={`prediction-${match.id}-${val}`}
        className={`relative flex-1 rounded-lg border-2 py-3 font-heading font-bold text-xl transition-all disabled:cursor-not-allowed ${cls}`}
      >
        {label}
        {wasSaved && <CheckCircle2 size={14} className="inline ml-1 mb-1" />}
      </button>
    );
  };

  return (
    <div className="surface p-4 sm:p-5 animate-fade-up" data-testid={`match-card-${match.id}`}>
      <div className="flex items-center justify-between mb-3 text-xs">
        <span className="overline-label !tracking-wider">{match.competition_name}{match.matchday ? ` · G${match.matchday}` : ""}</span>
        <span className={`inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded-full ${
          isLive ? "text-status-live bg-status-live/10" : isFinished ? "text-status-correct bg-status-correct/10" : locked ? "text-status-locked bg-status-locked/10" : "text-slate-400 bg-ink-700"}`}>
          {isLive && <Radio size={12} className="animate-pulse-live" />}
          {locked && !isLive && !isFinished && <Lock size={12} />}
          {STATUS_LABEL[match.status] || match.status}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
          <TeamCrest name={match.home_team} short={match.home_short} logo={match.home_logo} size={52} />
          <span className="text-sm font-semibold text-center text-white truncate w-full">{match.home_team}</span>
        </div>
        <div className="flex flex-col items-center px-2">
          {isFinished || isLive ? (
            <div className="font-heading text-3xl font-black text-white tabular-nums">
              {match.home_score ?? 0} <span className="text-gold-300">:</span> {match.away_score ?? 0}
            </div>
          ) : (
            <div className="text-muted-foreground text-xs text-center leading-tight">
              <Clock size={16} className="mx-auto mb-1 text-gold-300" />
              {fmtDateTime(match.kickoff)}
            </div>
          )}
        </div>
        <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
          <TeamCrest name={match.away_team} short={match.away_short} logo={match.away_logo} size={52} />
          <span className="text-sm font-semibold text-center text-white truncate w-full">{match.away_team}</span>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <Option val="1" label="1" />
        <Option val="X" label="X" />
        <Option val="2" label="2" />
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 min-h-[2.2rem]">
        {!locked ? (
          <>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              {savedChoice && !dirty ? (
                <span className="inline-flex items-center gap-1 text-status-correct font-medium" data-testid={`prediction-confirmed-${match.id}`}>
                  <CheckCircle2 size={13} /> Confermato: {savedChoice}
                </span>
              ) : (
                <><Lock size={12} /> Chiusura tra <Countdown target={match.deadline} compact onExpire={() => setLocked(true)} /></>
              )}
            </div>
            {canPredict ? (
              (savedChoice && !dirty) ? (
                <span className="text-xs text-muted-foreground">Modificabile fino alla chiusura</span>
              ) : (
                <button disabled={!dirty || saving} onClick={submit} data-testid={`prediction-confirm-${match.id}`} className="btn-gold !py-2 !px-4 text-sm">
                  {saving ? "Salvo…" : savedChoice ? "Aggiorna" : "Conferma"}
                </button>
              )
            ) : (
              <span className="text-xs text-gold-300">Abbonati per pronosticare</span>
            )}
          </>
        ) : (
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Lock size={12} /> Pronostici chiusi
            {savedChoice && <span className="ml-1 text-slate-300">· Hai scelto <b className="text-gold-300">{savedChoice}</b></span>}
            {isFinished && savedChoice && (
              <span className={`ml-1 font-semibold ${savedChoice === match.result ? "text-status-correct" : "text-destructive"}`}>
                {savedChoice === match.result ? "+3 punti" : "0 punti"}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
